/**
 * The public service detail page, loaded with one PostgREST read, and the
 * query keys it shares with the application form (serviceFormQuery.ts).
 *
 * The detail page used to await seven reads one after another, and the form
 * five before its first field. Every one of those reads takes 3-24 ms on the
 * server; the pages took 1.4-2.0 s and 1.1-1.6 s on a desktop in Guyana, and
 * 5.3 s and 4.1-4.7 s on a phone, because each read is a round trip
 * (docs/plans/public-pages-performance-2026-10-16.md, section 3.1). Every
 * child table has a foreign key to es_services, so PostgREST returns the
 * service and its children in one response, each child list filtered and
 * ordered as its own read was.
 *
 * RLS still decides: each embedded table's own policies apply to the embed
 * exactly as they did to the separate read.
 *
 * Both pages load through react-query under the keys below, so a revisit is
 * served from the cache, and an answer for a slug the visitor has left is
 * filed under that slug instead of landing on the page they moved to.
 */

import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/services/supabase";
import type { ServiceWithDetails } from "@/services/serviceCreatorService";

/**
 * One key per published service, with the detail page and the form under it.
 * A staff preview reads drafts under the staff member's own RLS, so it never
 * shares an entry with the public.
 */
export const publicServiceKeys = {
  all: ["services", "public"] as const,
  service: (slug: string) => [...publicServiceKeys.all, slug] as const,
  detail: (slug: string) =>
    [...publicServiceKeys.service(slug), "detail"] as const,
  form: (slug: string) => [...publicServiceKeys.service(slug), "form"] as const,
  preview: (serviceId: string) =>
    ["services", "preview", serviceId, "form"] as const,
};

const DETAIL_SELECT =
  "*, es_service_requirements(*), es_service_process_steps(*), es_service_faqs(*), es_service_resources(*)";

/**
 * Fetch a published service by slug with all related data
 */
export async function getPublishedServiceBySlug(slug: string): Promise<{
  data: ServiceWithDetails | null;
  error: Error | null;
}> {
  try {
    // Only published/active. The form and its fields are not read: the page
    // never renders them.
    const { data: row, error: serviceError } = await supabase
      .from("es_services")
      .select(DETAIL_SELECT)
      .eq("slug", slug)
      .eq("status", "published")
      .eq("is_active", true)
      .order("display_order", { referencedTable: "es_service_requirements" })
      .order("step_number", { referencedTable: "es_service_process_steps" })
      .order("display_order", { referencedTable: "es_service_faqs" })
      .order("display_order", { referencedTable: "es_service_resources" })
      .single();

    if (serviceError) {
      if (serviceError.code === "PGRST116") {
        return { data: null, error: new Error("Service not found") };
      }
      throw serviceError;
    }

    const {
      es_service_requirements: requirements,
      es_service_process_steps: processSteps,
      es_service_faqs: faqs,
      es_service_resources: resources,
      ...service
    } = row;

    const serviceWithDetails: ServiceWithDetails = {
      ...service,
      requirements: requirements || [],
      process_steps: processSteps || [],
      faqs: faqs || [],
      resources: resources || [],
    };

    return { data: serviceWithDetails, error: null };
  } catch (err) {
    console.error("Error fetching service:", err);
    return {
      data: null,
      error: err instanceof Error ? err : new Error("Failed to fetch service"),
    };
  }
}

/**
 * The detail page's query. A failed read is not retried, as before: a missing
 * service is an answer, not a fault.
 */
export function publicServiceDetailQuery(slug: string) {
  return queryOptions({
    queryKey: publicServiceKeys.detail(slug),
    queryFn: async (): Promise<ServiceWithDetails> => {
      const { data, error } = await getPublishedServiceBySlug(slug);
      if (error || !data) throw error ?? new Error("Service not found");
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
